/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useIdempotencyKey, idempotencyHeader } from "@/lib/idempotency";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { Button } from "@/components/ui/button";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceDictateButton } from "@/components/journal/VoiceDictateButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChildPicker, validateChildSelection } from "@/components/ChildPicker";
import { Plus, Inbox, Send, CalendarDays, Phone, Video, MessageSquare, Check, X, Pencil, Ban, Bell, GraduationCap, Stethoscope, Plane, Mic, Paperclip, Users as UsersIcon, Clock as ClockIcon } from "lucide-react";
import { InfoTooltip } from "@/components/InfoTooltip";
import { ChildDots } from "@/components/ChildDots";
import { formatDT, toLocalInput, fromLocalInput, formatDate } from "@/lib/format";
import { toast } from "sonner";

const TYPE_META = {
  schedule_change: { label: "Schedule change", icon: CalendarDays,   tone: "bg-[#E3EAF3] text-[#2E4A73] border-[#B8C7DD]" },
  phone_call:      { label: "Phone call",      icon: Phone,          tone: "bg-[#E6EFF4] text-[#3D5C6B] border-[#B6CFDD]" },
  video_call:      { label: "Video call",      icon: Video,          tone: "bg-[#ECE3F3] text-[#4E3877] border-[#C6B2DD]" },
  // v77.99.108d (iter 122.req-overhaul) — calmer, parent-focused
  // request categories. Backend `REQUEST_TYPES` was extended in lock-
  // step.
  school:          { label: "School request",  icon: GraduationCap,  tone: "bg-[#E8EFE4] text-[#3F5A2C] border-[#C5D5B8]" },
  medical:         { label: "Medical request", icon: Stethoscope,    tone: "bg-[#F4E7E7] text-[#7A2E2E] border-[#E0B8B8]" },
  holiday:         { label: "Holiday request", icon: Plane,          tone: "bg-[#F4ECDC] text-[#705828] border-[#D9C28F]" },
  other:           { label: "Other request",   icon: MessageSquare,  tone: "bg-[#EEF3F4] text-[#2E3A3F] border-[#D9E1E3]" },
};

// v77.99.108d (iter 122.req-overhaul) — calm voice-dictation row.
// Per user spec: shorter button label, no explanatory paragraph,
// just one concise sentence reassuring the parent that they can
// review the transcript before sending.
function RequestVoiceRow({ testidPrefix, onTranscript, disabled }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mt-1.5">
      <VoiceDictateButton
        testidPrefix={testidPrefix}
        onTranscript={onTranscript}
        disabled={disabled}
        label="Dictate response"
      />
      <span className="text-[11px] text-[#6F828A] leading-snug">
        Your response will appear above for you to review before sending.
      </span>
    </div>
  );
}



const STATUS_META = {
  pending:    { label: "Pending",    className: "border-[#B88A3A] bg-[#FBF0DC] text-[#6F4E1E]" },
  agreed:     { label: "Agreed",     className: "border-[#1F9FA4] bg-[#D6F0EF] text-[#116B6E]" },
  not_agreed: { label: "Not agreed", className: "border-[#8C2A22] bg-[#FBEFED] text-[#8C2A22]" },
  amended:    { label: "Amended",    className: "border-[#7A5AA6] bg-[#ECE3F3] text-[#4E3877]" },
  cancelled:  { label: "Cancelled",  className: "border-[#8A8A8A] bg-[#EAEAEA] text-[#6A6A6A]" },
};

const ACTION_LABEL = {
  created:    "Created",
  agreed:     "Accepted",
  not_agreed: "Declined",
  amended:    "Suggested changes",
  cancelled:  "Cancelled",
};

export default function Requests() {
  const { user, members, isMediator } = useAuth();
  const { selectedChildId } = useChildScope();
  const [tab, setTab] = useState("incoming"); // 'incoming' | 'outgoing'
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [children, setChildren] = useState([]);
  // v76.7 — child profile field change requests (locked-field proposals).
  // Fetched from a single household-wide endpoint so the primary parent
  // never has to drill into each child profile to discover them. The API
  // already filters by visible-child scope and (for co-parents) only
  // returns proposals they submitted themselves.
  const [profileProposals, setProfileProposals] = useState([]);
  const [proposalBusy, setProposalBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [detailFor, setDetailFor] = useState(null);
  // v77.99.150 (iter 122.loading-states) — Explicit loading flag so the
  // "No requests to review right now." empty state never renders before
  // the first /requests response arrives.
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const childQs = selectedChildId ? `?child_id=${encodeURIComponent(selectedChildId)}` : "";
    try {
      const [{ data: rq }, { data: evs }, { data: kids }, propsRes] = await Promise.all([
        api.get(`/requests${childQs}`),
        api.get(`/events${childQs}`).catch(() => ({ data: [] })),
        api.get("/children?include_archived=false").catch(() => ({ data: [] })),
        api.get(`/children/proposals/pending${childQs}`).catch(() => ({ data: [] })),
      ]);
      setItems(rq || []);
      setEvents(evs || []);
      setChildren(kids || []);
      setProfileProposals(propsRes.data || []);
    } finally {
      setLoading(false);
    }
  }, [selectedChildId]);
  useEffect(() => { load(); }, [load]);

  const decideProposal = async (p, action) => {
    if (action === "reject" && !window.confirm(`Decline ${p.proposed_by_name || "the co-parent"}'s suggestion to update ${p.label}?`)) return;
    setProposalBusy(true);
    try {
      await api.post(
        `/children/${p.child_id}/proposals/${p.id}/${action}`,
        { note: "" },
      );
      toast.success(action === "approve" ? "Change applied" : "Suggestion declined");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't decide on this change request");
    } finally {
      setProposalBusy(false);
    }
  };

  // v76.8 — Friendly one-click reminder. Server gates: must be the
  // proposer, ≥7 days old, ≤1 nudge per 24h. UI hides the button when
  // those conditions aren't met, but the API still defends.
  const sendNudge = async (p) => {
    setProposalBusy(true);
    try {
      await api.post(`/children/proposals/${p.id}/nudge`);
      toast.success("Reminder sent. The primary parent will see it in-app.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send the reminder");
    } finally {
      setProposalBusy(false);
    }
  };

  const nameOf = (uid) => {
    const m = members.find((x) => x.id === uid);
    return m?.name || (uid === user?.id ? user?.name : "Co-parent");
  };

  const incoming = items.filter((r) => r.created_by !== user?.id);
  const outgoing = items.filter((r) => r.created_by === user?.id);
  const incomingPending = incoming.filter((r) => r.status === "pending").length;
  const outgoingPending = outgoing.filter((r) => r.status === "pending").length;
  const list = tab === "incoming" ? incoming : outgoing;

  return (
    <div className="space-y-6" data-testid="requests-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold inline-flex items-center gap-1.5">
            Requests
            <InfoTooltip
              text="Pending: waiting on a response. Accepted: both parents agree. Declined: turned down. Cancelled: withdrawn by the sender. Every action is kept in the activity history."
              testid="help-tooltip-request-status"
            />
          </h1>
          <p className="text-sm text-[#6F828A] mt-1 max-w-xl">
            A structured space to propose changes, calls or other arrangements.
            Nothing changes on the calendar until both parents agree.
          </p>
        </div>
        {!isMediator && (
          <Button onClick={() => setNewOpen(true)} className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm" data-testid="new-request-button">
            <Plus className="w-4 h-4 mr-2" /> New request
          </Button>
        )}
      </header>

      {/* v76.7 — Profile change requests. Surface pending child profile
          field updates here so the primary parent doesn't have to open
          each child profile to discover them. The API already returns
          only proposals in the caller's scope: primary sees all
          household pending across visible children; a co-parent sees
          only their own outstanding submissions. */}
      {profileProposals.length > 0 && (
        <section
          className="border border-[#E6CFA2] bg-[#FDF4E2] rounded-md p-4 sm:p-5"
          data-testid="profile-change-requests"
        >
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="font-heading text-lg font-medium text-[#2E3A3F] inline-flex items-center gap-2">
              <Pencil className="w-4 h-4 text-[#8F6B1F]" />
              Profile change requests
              <span
                className="text-[10px] uppercase tracking-[0.1em] text-[#8F6B1F] bg-[#FDEBC1] border border-[#E6CFA2] rounded-sm px-1.5 py-0.5"
                data-testid="profile-change-requests-count"
              >
                {profileProposals.length} awaiting
              </span>
            </h2>
            <p className="text-xs text-[#6F5318]">
              {user?.is_primary_parent
                ? "Co-parents have suggested changes to locked profile sections. Review and approve or decline."
                : "Your suggested changes are awaiting the primary parent's review."}
            </p>
          </div>
          <ul className="mt-4 space-y-2.5" data-testid="profile-change-requests-list">
            {profileProposals.map((p) => {
              const sectionLabel = ((p.section || "general") + "").replace(/^\w/, (c) => c.toUpperCase());
              return (
                <li
                  key={p.id}
                  className="border border-[#E6CFA2] bg-white rounded-sm p-3"
                  data-testid={`profile-change-request-${p.id}`}
                >
                  <div className="flex items-baseline gap-2 flex-wrap text-xs">
                    <span className="font-medium text-[#2E3A3F]">{p.child_name || "Child"}</span>
                    <span className="text-[10px] uppercase tracking-[0.1em] text-[#8F6B1F] bg-[#FDEBC1] border border-[#E6CFA2] rounded-sm px-1.5 py-0.5">
                      {sectionLabel === "Emergency" ? "Emergency contact" : sectionLabel}
                    </span>
                    <span className="text-[#6F828A] ml-auto">{formatDT(p.proposed_at)}</span>
                  </div>
                  <div className="mt-1.5 text-sm font-medium text-[#2E3A3F]">{p.label}</div>
                  <div className="mt-1.5 text-sm flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-1 rounded-sm bg-[#FAFBFB] border border-[#D9E1E3] text-[#825A41] text-xs">
                      {p.old_value || "Not set"}
                    </span>
                    <span className="text-[#6F828A]">→</span>
                    <span className="px-2 py-1 rounded-sm bg-[#E0F2F2] border border-[#A6D7D9] text-[#116B6E] text-xs">
                      {p.new_value || "Not set"}
                    </span>
                  </div>
                  {p.note && <p className="mt-1.5 text-xs text-[#6F828A] italic">"{p.note}"</p>}
                  <div className="mt-2 text-[11px] text-[#6F828A]">
                    Requested by <span className="text-[#2E3A3F] font-medium">{p.proposed_by_name || "Co-parent"}</span>
                    {p.last_nudged_at && (
                      <span> · last reminder {formatDT(p.last_nudged_at)}</span>
                    )}
                  </div>
                  {user?.is_primary_parent && (
                    <div className="mt-3 flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => decideProposal(p, "reject")}
                        disabled={proposalBusy}
                        className="border-[#D9E1E3] rounded-sm h-8 text-xs"
                        data-testid={`profile-change-request-${p.id}-reject`}
                      >
                        <X className="w-3 h-3 mr-1" /> Decline
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => decideProposal(p, "approve")}
                        disabled={proposalBusy}
                        className="bg-[#1F9FA4] hover:bg-[#178085] h-8 text-xs"
                        data-testid={`profile-change-request-${p.id}-approve`}
                      >
                        <Check className="w-3 h-3 mr-1" /> Approve
                      </Button>
                    </div>
                  )}
                  {/* v76.8 — Proposer-side reminder affordance. Only
                      renders for the original proposer once the request
                      has been waiting for ≥7 days. The button is
                      disabled if a reminder was sent in the last 24h
                      (server enforces this too). One click → primary
                      gets a "Reminder: ..." notification in-app. */}
                  {!user?.is_primary_parent && p.proposed_by === user?.id && (() => {
                    const proposedAt = p.proposed_at ? new Date(p.proposed_at) : null;
                    if (!proposedAt) return null;
                    const ageDays = Math.floor((Date.now() - proposedAt.getTime()) / 86400000);
                    if (ageDays < 7) return null;
                    const lastNudged = p.last_nudged_at ? new Date(p.last_nudged_at) : null;
                    const cooldownActive = lastNudged && (Date.now() - lastNudged.getTime() < 24 * 3600 * 1000);
                    return (
                      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-xs text-[#6F4E1E]">
                          Waiting {ageDays} days. You can send a friendly reminder.
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => sendNudge(p)}
                          disabled={proposalBusy || cooldownActive}
                          className="border-[#D9E1E3] rounded-sm h-8 text-xs"
                          data-testid={`profile-change-request-${p.id}-nudge`}
                          title={cooldownActive ? "You can send another reminder in 24 hours" : "Send a friendly in-app reminder"}
                        >
                          <Bell className="w-3 h-3 mr-1" /> {cooldownActive ? "Reminder sent" : "Send a reminder"}
                        </Button>
                      </div>
                    );
                  })()}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Tab switcher */}
      <div className="flex items-center gap-0 border border-[#D9E1E3] bg-white rounded-md overflow-hidden w-full sm:w-auto" data-testid="requests-tabs">
        <button
          type="button"
          onClick={() => setTab("incoming")}
          className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm transition-colors border-r border-[#D9E1E3] ${tab === "incoming" ? "bg-[#E8F0F1] text-[#2E3A3F] font-medium" : "text-[#6F828A] hover:bg-[#EEF3F4]"}`}
          data-testid="tab-incoming"
        >
          <Inbox className="w-4 h-4" />
          To review
          {incomingPending > 0 && (
            <span className="inline-flex items-center justify-center text-[10px] font-medium px-1.5 py-0.5 bg-[#B88A3A] text-white rounded-sm" data-testid="incoming-pending-count">{incomingPending}</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setTab("outgoing")}
          className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm transition-colors ${tab === "outgoing" ? "bg-[#E8F0F1] text-[#2E3A3F] font-medium" : "text-[#6F828A] hover:bg-[#EEF3F4]"}`}
          data-testid="tab-outgoing"
        >
          <Send className="w-4 h-4" />
          My requests
          {outgoingPending > 0 && (
            <span className="inline-flex items-center justify-center text-[10px] font-medium px-1.5 py-0.5 bg-[#B88A3A] text-white rounded-sm" data-testid="outgoing-pending-count">{outgoingPending}</span>
          )}
        </button>
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="requests-list">
        {list.length === 0 && (
          <div className="border border-dashed border-[#D9E1E3] bg-white rounded-md p-10 text-center text-[#6F828A]" data-testid={loading ? "requests-loading" : `requests-empty-${tab}`}>
            {loading ? (
              <>
                <Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" strokeWidth={1.5} />
                Loading requests…
              </>
            ) : tab === "incoming" ? (
              <>
                <Inbox className="w-6 h-6 mx-auto mb-2" strokeWidth={1.5} />
                No requests to review right now.
              </>
            ) : (
              <>
                <Send className="w-6 h-6 mx-auto mb-2" strokeWidth={1.5} />
                You haven't made any requests yet.
              </>
            )}
          </div>
        )}
        {list.map((r) => (
          <RequestRow
            key={r.id}
            req={r}
            events={events}
            members={members}
            me={user}
            onOpen={() => setDetailFor(r)}
            nameOf={nameOf}
          />
        ))}
      </div>

      {/* New request dialog */}
      <NewRequestDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        events={events}
        childList={children}
        onCreated={() => { setNewOpen(false); load(); }}
      />

      {/* Detail / actions dialog */}
      <RequestDetailDialog
        req={detailFor}
        events={events}
        members={members}
        me={user}
        nameOf={nameOf}
        householdChildren={children}
        onClose={() => setDetailFor(null)}
        onChanged={() => { setDetailFor(null); load(); }}
      />
    </div>
  );
}

function RequestRow({ req, events, members, me, onOpen, nameOf }) {
  const meta = TYPE_META[req.type] || TYPE_META.other;
  const Icon = meta.icon;
  const statusMeta = STATUS_META[req.status] || STATUS_META.pending;
  const event = req.target_event_id ? events.find((e) => e.id === req.target_event_id) : null;
  const isMine = req.created_by === me?.id;
  const requestedDate = req.proposed?.start || event?.start || null;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left border border-[#D9E1E3] bg-white rounded-md p-4 hover:border-[#1F9FA4]/40 hover:bg-[#F2FAFA] transition-colors"
      data-testid={`request-row-${req.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-sm border ${meta.tone}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm ${statusMeta.className}`} data-testid={`request-status-${req.id}`}>
              {statusMeta.label}
            </span>
            <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm border-[#D9E1E3] text-[#6F828A]" data-testid={`request-type-${req.id}`}>
              {meta.label}
            </span>
          </div>
          <div className="mt-1.5 text-sm text-[#2E3A3F]">
            <span className="font-medium">{isMine ? "You" : nameOf(req.created_by)}</span>{" "}
            {req.type === "schedule_change" && event && (
              <>requested a change to <span className="font-medium">{event.title}</span></>
            )}
            {req.type === "phone_call" && <>requested a phone call</>}
            {req.type === "video_call" && <>requested a video call</>}
            {req.type === "other" && <>made a request</>}
          </div>
          {req.message && (
            <div className="mt-1 text-xs text-[#6F828A] line-clamp-2">“{req.message}”</div>
          )}
          <div className="mt-1.5 text-xs text-[#6F828A] font-mono-data flex items-center gap-3 flex-wrap">
            <span>Submitted {formatDate(req.created_at)}</span>
            {requestedDate && (
              <span>· Requested date: {formatDT(requestedDate)}</span>
            )}
            {/* v65 master-view child indicator. Falls back to the linked
                event's child_ids when the request itself isn't tagged
                so legacy schedule-change requests still show context. */}
            <ChildDots childIds={(req.child_ids && req.child_ids.length > 0) ? req.child_ids : (event?.child_ids || [])} />
          </div>
        </div>
      </div>
    </button>
  );
}

function NewRequestDialog({ open, onOpenChange, events, childList, onCreated }) {
  const { user } = useAuth();
  const { selectedChildId, children: scopeChildren } = useChildScope();
  const [type, setType] = useState("schedule_change");
  const [targetEventId, setTargetEventId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");
  const [reqChildIds, setReqChildIds] = useState([]);
  const [busy, setBusy] = useState(false);
  // v77.99.18 — Per-form idempotency key.
  const { key: idemKey, rotate: rotateIdemKey } = useIdempotencyKey();

  const confirmedEvents = useMemo(() => (events || []).filter((e) => e.status !== "proposed").sort((a, b) => a.start.localeCompare(b.start)), [events]);

  useEffect(() => {
    if (open) {
      setType("schedule_change");
      setTargetEventId("");
      setStart("");
      setEnd("");
      setTitle("");
      setNotes("");
      setMessage("");
      setReqChildIds(selectedChildId ? [selectedChildId] : []);
    }
  }, [open, selectedChildId]);

  // For schedule_change requests, mirror the chosen event's child_ids
  // so the user doesn't have to re-pick the children covered by an
  // already-tagged event. Stops mirroring once they touch the picker.
  const [childPickerTouched, setChildPickerTouched] = useState(false);
  useEffect(() => {
    if (childPickerTouched) return;
    if (type !== "schedule_change") return;
    if (!targetEventId) return;
    const ev = (events || []).find((e) => e.id === targetEventId);
    if (ev && Array.isArray(ev.child_ids) && ev.child_ids.length > 0) {
      setReqChildIds(ev.child_ids);
    }
  }, [type, targetEventId, events, childPickerTouched]);
  const onChildPickerChange = (next) => {
    setChildPickerTouched(true);
    setReqChildIds(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const proposed = {};
      if (type === "schedule_change") {
        if (!targetEventId) { toast.error("Please pick the event you want to change"); setBusy(false); return; }
        if (start) proposed.start = fromLocalInput(start);
        if (end) proposed.end = fromLocalInput(end);
        if (notes) proposed.notes = notes;
        if (!proposed.start && !proposed.end && !proposed.notes) {
          toast.error("Please specify what you want to change (start, end or notes)");
          setBusy(false); return;
        }
      } else if (type === "phone_call" || type === "video_call") {
        if (!start) { toast.error("Please pick a date and time for the call"); setBusy(false); return; }
        proposed.start = fromLocalInput(start);
        proposed.end = end ? fromLocalInput(end) : proposed.start;
        if (title) proposed.title = title;
        if (notes) proposed.notes = notes;
      } else if (type === "holiday") {
        if (!start) { toast.error("Please pick a start date"); setBusy(false); return; }
        proposed.start = fromLocalInput(start);
        if (end) proposed.end = fromLocalInput(end);
        if (title) proposed.title = title;
        if (notes) proposed.notes = notes;
      } else if (type === "school" || type === "medical" || type === "other") {
        if (!message.trim()) { toast.error("Please describe your request"); setBusy(false); return; }
        if (title) proposed.title = title;
        if (notes) proposed.notes = notes;
      }
      const validationErr = validateChildSelection({
        value: reqChildIds,
        hasChildren: (scopeChildren || []).length > 0,
        selectedChildId,
      });
      if (validationErr) { toast.error(validationErr); setBusy(false); return; }
      const payload = {
        type,
        target_event_id: type === "schedule_change" ? targetEventId : null,
        proposed,
        message,
        child_ids: reqChildIds,
      };
      // v77.99.18 — Idempotency-Key on create_request.
      await api.post("/requests", payload, { headers: idempotencyHeader(idemKey) });
      rotateIdemKey();
      toast.success("Request sent for review");
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="new-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New request</DialogTitle>
          <DialogDescription className="text-[#6F828A]">
            Propose a change, a call, or another arrangement. It stays a request until your co-parent agrees.
          </DialogDescription>
        </DialogHeader>
        <UpgradePrompt feature="create requests" testid="requests-upgrade-prompt" />
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>What kind of request?</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="new-request-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="schedule_change">Schedule change</SelectItem>
                <SelectItem value="phone_call">Phone call</SelectItem>
                <SelectItem value="video_call">Video call</SelectItem>
                <SelectItem value="school">School request</SelectItem>
                <SelectItem value="medical">Medical request</SelectItem>
                <SelectItem value="holiday">Holiday request</SelectItem>
                <SelectItem value="other">Other request</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-[#6F828A] mt-1">
              {type === "schedule_change" && "Propose a change to an agreed event. Nothing on the calendar changes until your co-parent agrees."}
              {type === "phone_call" && "Propose a date and time for a phone call with your child. Creates a calendar event once agreed."}
              {type === "video_call" && "Propose a date and time for a video call. Creates a calendar event once agreed."}
              {type === "school" && "Anything school-related you'd like agreed, such as a parents' evening, school trip permission or change of contact."}
              {type === "medical" && "Anything medical that needs agreement, such as appointments, treatment decisions or medication changes."}
              {type === "holiday" && "Propose a holiday or trip with the children. Dates, destination and any travel arrangements."}
              {type === "other" && "Anything else that needs to be agreed. For example, swapping a weekend or an overnight at grandparents."}
            </p>
          </div>

          {type === "schedule_change" && (
            <div>
              <Label>Which event?</Label>
              <Select value={targetEventId} onValueChange={setTargetEventId}>
                <SelectTrigger data-testid="new-request-event"><SelectValue placeholder="Pick an event" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {confirmedEvents.length === 0 && (
                    <div className="px-3 py-2 text-xs text-[#6F828A]">No events available. Add one on the Calendar first.</div>
                  )}
                  {confirmedEvents.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.title} · {formatDT(e.start)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {(type === "schedule_change" || type === "phone_call" || type === "video_call" || type === "holiday") && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{type === "schedule_change" ? "New start (optional)" : type === "holiday" ? "Start date" : "Start"}</Label>
                <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="new-request-start" />
              </div>
              <div>
                <Label>{type === "schedule_change" ? "New end (optional)" : type === "holiday" ? "End date" : "End (optional)"}</Label>
                <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="new-request-end" />
              </div>
            </div>
          )}

          {(type === "phone_call" || type === "video_call" || type === "school" || type === "medical" || type === "holiday" || type === "other") && (
            <div>
              <Label>Title (optional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder={
                  type === "school"   ? "e.g. Parents' evening" :
                  type === "medical"  ? "e.g. Dental check-up" :
                  type === "holiday"  ? "e.g. Half-term break in Cornwall" :
                  type === "other"    ? "Short summary" :
                  "e.g. Bedtime call"
                }
                data-testid="new-request-title" />
            </div>
          )}

          <div>
            <Label>Note to your co-parent</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
              placeholder="Keep it short and neutral. Explain why you'd like this and any flexibility you have."
              data-testid="new-request-message" />
            {/* v77.99.54 — Voice-to-text drop-in (reuses Journal/Messages component). */}
            <RequestVoiceRow
              testidPrefix="new-request-message-voice"
              onTranscript={(t) =>
                setMessage((prev) => (prev ? prev + (prev.endsWith(" ") ? "" : " ") : "") + t)
              }
              disabled={busy}
            />
          </div>

          <ChildPicker
            value={reqChildIds}
            onChange={onChildPickerChange}
            testPrefix="new-request-child-picker"
            label="Which child does this request relate to?"
            hint="Schedule-change requests pre-fill from the linked event. Adjust if needed."
          />

          {(type === "schedule_change" || type === "school" || type === "medical" || type === "holiday" || type === "other") && (
            <div>
              <Label>Additional notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="new-request-notes" />
              <RequestVoiceRow
                testidPrefix="new-request-notes-voice"
                onTranscript={(t) =>
                  setNotes((prev) => (prev ? prev + (prev.endsWith(" ") ? "" : " ") : "") + t)
                }
                disabled={busy}
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="new-request-cancel">Cancel</Button>
            <Button type="submit" className="bg-[#1F9FA4] hover:bg-[#178085]" disabled={busy} data-testid="new-request-submit">
              {busy ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RequestDetailDialog({ req, events, members, me, nameOf, householdChildren, onClose, onChanged }) {
  const [decisionReason, setDecisionReason] = useState("");
  const [amending, setAmending] = useState(false);
  const [amendStart, setAmendStart] = useState("");
  const [amendEnd, setAmendEnd] = useState("");
  const [amendMessage, setAmendMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (req) {
      setDecisionReason("");
      setAmending(false);
      setAmendStart(req.proposed?.start ? toLocalInput(req.proposed.start) : "");
      setAmendEnd(req.proposed?.end ? toLocalInput(req.proposed.end) : "");
      setAmendMessage("");
    }
  }, [req]);

  if (!req) return null;
  const meta = TYPE_META[req.type] || TYPE_META.other;
  const statusMeta = STATUS_META[req.status] || STATUS_META.pending;
  const event = req.target_event_id ? events.find((e) => e.id === req.target_event_id) : null;
  const isMine = req.created_by === me?.id;
  const canReview = !isMine && req.status === "pending";
  const canCancel = isMine && req.status === "pending";

  // v77.99.108d (iter 122.req-overhaul) — Children names. The request
  // model carries `child_ids`; we look them up against the household
  // roster so the parent sees real names, not UUIDs.
  const childNames = (req.child_ids || [])
    .map((id) => (householdChildren || []).find((c) => c.id === id)?.first_name)
    .filter(Boolean);
  // Attachments / response deadline — forward-compatible: if a future
  // iteration attaches files or a deadline, the UI lights up. Today
  // these blocks render nothing.
  const attachments = Array.isArray(req.attachments) ? req.attachments : [];
  const responseDeadline = req.response_deadline || null;

  const doAgree = async () => {
    setBusy(true);
    try {
      await api.post(`/requests/${req.id}/agree`, {});
      toast.success("Request accepted");
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doNotAgree = async () => {
    if (!decisionReason.trim()) { toast.error("Please share a brief reason so the other parent can understand."); return; }
    setBusy(true);
    try {
      await api.post(`/requests/${req.id}/not-agree`, { reason: decisionReason.trim() });
      toast.success("Response sent");
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doCancel = async () => {
    if (!window.confirm("Withdraw this request? It will stay visible in the history.")) return;
    setBusy(true);
    try {
      await api.post(`/requests/${req.id}/cancel`);
      toast.success("Request withdrawn");
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doAmend = async () => {
    const proposed = { ...(req.proposed || {}) };
    if (amendStart) proposed.start = fromLocalInput(amendStart); else delete proposed.start;
    if (amendEnd) proposed.end = fromLocalInput(amendEnd); else delete proposed.end;
    if (req.type === "schedule_change" && !proposed.start && !proposed.end && !proposed.notes) {
      toast.error("Please suggest at least one change"); return;
    }
    if (req.type !== "schedule_change" && !proposed.start) {
      toast.error("Please pick a start time"); return;
    }
    setBusy(true);
    try {
      await api.post(`/requests/${req.id}/amend`, { proposed, message: amendMessage });
      toast.success("Suggestion sent");
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!req} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="request-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
            {meta.label}
            <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm ${statusMeta.className}`} data-testid="request-detail-status">
              {statusMeta.label}
            </span>
          </DialogTitle>
          <DialogDescription className="text-[#6F828A]">
            This page shows the complete history of this request, including who created it, any changes made and the final outcome.
          </DialogDescription>
          {responseDeadline && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm bg-[#FBF0DC] text-[#6F4E1E] border border-[#E2C588] w-fit"
              data-testid="request-detail-deadline">
              <ClockIcon className="w-3 h-3" /> Response requested by {formatDate(responseDeadline)}
            </div>
          )}
        </DialogHeader>
        <div className="space-y-5 text-sm">
          <RequestSection title="Created by" testid="request-section-created-by">
            <div>
              {isMine ? "You" : nameOf(req.created_by)}
              <span className="text-[#6F828A]"> on {formatDT(req.created_at)}</span>
            </div>
          </RequestSection>

          {childNames.length > 0 && (
            <RequestSection title="Children" testid="request-section-children">
              <div className="inline-flex items-center gap-1.5 text-[#2E3A3F]">
                <UsersIcon className="w-3.5 h-3.5 text-[#6F828A]" />
                {childNames.join(", ")}
              </div>
            </RequestSection>
          )}

          {event && (
            <RequestSection title="Linked event" testid="request-section-event">
              <div>{event.title}</div>
              <div className="text-xs text-[#6F828A] font-mono-data">
                Currently scheduled for {formatDT(event.start)} to {formatDT(event.end)}
              </div>
            </RequestSection>
          )}

          {(req.proposed?.start || req.proposed?.end || req.proposed?.notes || req.proposed?.title) && (
            <RequestSection title="Request details" testid="request-section-details">
              <div className="bg-[#F5F7F8] rounded-md p-3 space-y-1">
                {req.proposed?.title && <div>Title: {req.proposed.title}</div>}
                {req.proposed?.start && <div className="font-mono-data text-[#2E3A3F]">Start: {formatDT(req.proposed.start)}</div>}
                {req.proposed?.end && <div className="font-mono-data text-[#2E3A3F]">End: {formatDT(req.proposed.end)}</div>}
                {req.proposed?.notes && <div className="text-[#2E3A3F] whitespace-pre-wrap mt-1">{req.proposed.notes}</div>}
              </div>
            </RequestSection>
          )}

          {req.message && (
            <RequestSection title="Message" testid="request-section-message">
              <div className="whitespace-pre-wrap text-[#2E3A3F]">{req.message}</div>
            </RequestSection>
          )}

          {req.status !== "pending" && (
            <RequestSection title="Outcome" testid="request-section-outcome">
              <div>
                {statusMeta.label} by {req.decided_by ? nameOf(req.decided_by) : "unknown"}
                {req.decided_at && <span className="text-[#6F828A] font-mono-data"> on {formatDT(req.decided_at)}</span>}
              </div>
              {req.decision_note && (
                <div className="mt-1.5 text-sm italic text-[#2E3A3F]">
                  &ldquo;{req.decision_note}&rdquo;
                </div>
              )}
              {req.amended_to && (
                <div className="mt-2 text-xs text-[#6F828A]">
                  A newer version of this request has replaced this one. Look in &ldquo;My requests&rdquo; or &ldquo;To review&rdquo;.
                </div>
              )}
            </RequestSection>
          )}

          {attachments.length > 0 && (
            <RequestSection title="Attachments" testid="request-section-attachments">
              <ul className="space-y-1">
                {attachments.map((att, i) => (
                  <li key={att.id || att.name || i} className="inline-flex items-center gap-2 text-sm" data-testid={`request-attachment-${i}`}>
                    <Paperclip className="w-3.5 h-3.5 text-[#6F828A]" />
                    {att.url ? (
                      <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-[#116B6E] hover:underline">
                        {att.name || `Attachment ${i + 1}`}
                      </a>
                    ) : (
                      <span>{att.name || `Attachment ${i + 1}`}</span>
                    )}
                    {att.size_bytes && <span className="text-[11px] text-[#6F828A]">({Math.round(att.size_bytes / 1024)} KB)</span>}
                  </li>
                ))}
              </ul>
            </RequestSection>
          )}

          {req.history && req.history.length > 0 && (
            <RequestSection title="Activity history" testid="request-section-history">
              <ol className="space-y-3">
                {req.history.map((h, i) => (
                  <li key={`${h.at}-${h.action}-${h.by}-${i}`} className="flex gap-3" data-testid={`request-history-${i}`}>
                    <span className="font-mono-data text-[11px] text-[#6F828A] tabular-nums w-24 flex-shrink-0 pt-0.5">
                      {formatDT(h.at)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="text-[#2E3A3F]">
                        {ACTION_LABEL[h.action] || h.action} by {nameOf(h.by) || "someone"}
                      </span>
                      {h.data?.reason && (
                        <span className="block text-xs text-[#6F828A] italic mt-0.5">
                          &ldquo;{h.data.reason}&rdquo;
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </RequestSection>
          )}

          {/* Suggest-changes panel */}
          {canReview && amending && (
            <div className="bg-[#F5F7F8] rounded-md p-3.5 space-y-3" data-testid="request-amend-panel">
              <div className="text-xs font-medium text-[#2E3A3F]">Suggest changes</div>
              <p className="text-xs text-[#6F828A] leading-snug">
                Your suggestion will be sent back to {nameOf(req.created_by)} for them to review. The original request stays in the history.
              </p>
              {(req.type === "schedule_change" || req.type === "phone_call" || req.type === "video_call") && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Start</Label>
                    <Input type="datetime-local" value={amendStart} onChange={(e) => setAmendStart(e.target.value)} data-testid="amend-start" />
                  </div>
                  <div>
                    <Label className="text-xs">End</Label>
                    <Input type="datetime-local" value={amendEnd} onChange={(e) => setAmendEnd(e.target.value)} data-testid="amend-end" />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">Your message</Label>
                <Textarea rows={2} value={amendMessage} onChange={(e) => setAmendMessage(e.target.value)} data-testid="amend-message" />
                <RequestVoiceRow
                  testidPrefix="amend-message-voice"
                  onTranscript={(t) =>
                    setAmendMessage((prev) => (prev ? prev + (prev.endsWith(" ") ? "" : " ") : "") + t)
                  }
                  disabled={busy}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setAmending(false)} disabled={busy} data-testid="amend-cancel">Back</Button>
                <Button type="button" onClick={doAmend} disabled={busy} className="bg-[#7A5AA6] hover:bg-[#61468B]" data-testid="amend-submit">Send suggestion</Button>
              </div>
            </div>
          )}

          {/* Cannot-agree reason panel */}
          {canReview && !amending && (
            <div data-testid="request-decline-panel">
              <Label className="text-xs text-[#6F828A]">Reason (required if you cannot agree)</Label>
              <Textarea
                rows={2}
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
                placeholder="Briefly explain why you cannot agree."
                data-testid="not-agree-reason"
              />
              <RequestVoiceRow
                testidPrefix="not-agree-reason-voice"
                onTranscript={(t) =>
                  setDecisionReason((prev) => (prev ? prev + (prev.endsWith(" ") ? "" : " ") : "") + t)
                }
                disabled={busy}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {canReview && !amending && (
            <>
              <Button type="button" variant="outline" onClick={() => setAmending(true)} className="border-[#D9E1E3]" disabled={busy} data-testid="request-amend-button">
                <Pencil className="w-4 h-4 mr-1.5" /> Suggest changes
              </Button>
              <Button type="button" onClick={doNotAgree} disabled={busy} className="bg-[#C0291D] hover:bg-[#A8251A] text-white" data-testid="request-not-agree-button">
                <X className="w-4 h-4 mr-1.5" /> Cannot agree
              </Button>
              <Button type="button" onClick={doAgree} disabled={busy} className="bg-[#1F9FA4] hover:bg-[#178085] text-white" data-testid="request-agree-button">
                <Check className="w-4 h-4 mr-1.5" /> Accept request
              </Button>
            </>
          )}
          {canCancel && (
            <Button type="button" onClick={doCancel} variant="outline" className="border-[#D9E1E3]" disabled={busy} data-testid="request-cancel-button">
              <Ban className="w-4 h-4 mr-1.5" /> Withdraw request
            </Button>
          )}
          {!canReview && !canCancel && (
            <Button type="button" variant="ghost" onClick={onClose} data-testid="request-detail-close">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// v77.99.108d (iter 122.req-overhaul) — Calm section heading used
// throughout the request detail dialog. Same uppercase-tracking eyebrow
// the rest of the app uses, but with sentence-cased labels and plain
// English bodies so the page reads as a conversation, not an audit
// report.
function RequestSection({ title, children, testid }) {
  return (
    <section data-testid={testid}>
      <h3 className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A] font-medium mb-1.5">
        {title}
      </h3>
      <div className="text-sm text-[#2E3A3F] leading-snug">{children}</div>
    </section>
  );
}
